import React, { useState, useEffect, Suspense } from "react";
import {
  signInWithCustomToken,
  signOut,
  onAuthStateChanged,
  signInAnonymously,
} from "firebase/auth";
import { collection, query, onSnapshot, doc, getDoc } from "firebase/firestore";

// Configuración y Utils
import { auth, db, appId } from "./config/firebase";
import { STORE_NAME } from "./utils/helpers";

// Layout y UI
import Header from "./components/layout/Header";
import Sidebar from "./components/layout/Sidebar";
import Loading from "./components/ui/Loading";

// --- CODE SPLITTING ---
const LandingPage = React.lazy(() => import("./views/public/LandingPage"));
const OrdersView = React.lazy(() => import("./views/admin/OrdersView"));
const KitchenView = React.lazy(() => import("./views/admin/KitchenView"));
const PantryView = React.lazy(() => import("./views/admin/PantryView"));
const FreezerView = React.lazy(() => import("./views/admin/FreezerView"));
const StatsView = React.lazy(() => import("./views/admin/StatsView"));
const ClientsView = React.lazy(() => import("./views/admin/ClientsView"));
const MenuView = React.lazy(() => import("./views/admin/MenuView"));
const ConfigView = React.lazy(() => import("./views/admin/ConfigView"));
const PublicStoreView = React.lazy(() =>
  import("./views/public/PublicStoreView")
);
const AuthView = React.lazy(() => import("./views/auth/AuthView"));
const MyCoursesView = React.lazy(() => import("./views/client/MyCoursesView"));
const CourseCatalogView = React.lazy(() =>
  import("./views/public/CourseCatalogView")
);

// Motor de Temas
const TailwindInjector = ({ theme }) => {
  useEffect(() => {
    if (!document.querySelector("#tailwind-script")) {
      const script = document.createElement("script");
      script.id = "tailwind-script";
      script.src = "https://cdn.tailwindcss.com";
      document.head.appendChild(script);
    }
  }, []);

  return (
    <style>
      {`
        :root {
          --color-primary: ${theme?.primary || "#4CAF50"};
          --color-secondary: ${theme?.secondary || "#dfc9ae"};
          --color-background: ${theme?.background || "#fffdf9"};
          --color-text: ${theme?.text || "#333333"};
          --color-navbar: ${theme?.navbar || "#ffffff"};
        }
      `}
    </style>
  );
};

// Estructura segura por defecto
const DEFAULT_CONFIG = {
  modules: {
    pantry: true, freezer: true, finances: true, clients: true,
    courses: true, coupons: true, flyer: true
  },
  general: {
    storeName: STORE_NAME, logoUrl: "", phone: "5493512343103",
    instagramUrl: "", storeInfoText: "",
  },
  theme: {
    primary: "#4CAF50", secondary: "#dfc9ae", background: "#fffdf9",
    text: "#333333", navbar: "#ffffff"
  },
  whatsapp: { customerOrderMessage: "", supplierOrderMessage: "", },
  categories: { pantry: [], expenses: [], income: [], },
  landing: {}, courses: [],
};

const App = () => {
  const [user, setUser] = useState(null);
  const [authChecking, setAuthChecking] = useState(true);
  
  const [configLoaded, setConfigLoaded] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [globalConfig, setGlobalConfig] = useState(DEFAULT_CONFIG);

  const [view, setView] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const mode = params.get("mode");
    if (mode === "store") return "public_store";
    if (mode === "courses_store") return "courses_store";
    if (mode === "admin") return "orders";
    if (mode === "courses") return "my_courses";
    if (mode === "auth") return "auth";
    return "landing";
  });

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [products, setProducts] = useState([]);
  const [clients, setClients] = useState([]);
  const [categories, setCategories] = useState([]);
  const [orders, setOrders] = useState([]);
  const [userRole, setUserRole] = useState(null);
  const [userData, setUserData] = useState(null);
  const [isHistoryLoaded, setIsHistoryLoaded] = useState(false);

  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    return () => window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") setDeferredPrompt(null);
  };

  const handleNavigate = (newView) => {
    setView(newView);
    let newUrl = "/";
    if (newView === "public_store") {
      newUrl = "?mode=store";
      localStorage.setItem("appMode", "client");
    } else if (newView === "courses_store") {
      newUrl = "?mode=courses_store";
      localStorage.setItem("appMode", "courses_store");
    } else if (newView === "landing") {
      newUrl = "/";
      localStorage.setItem("appMode", "client");
    } else if (newView === "my_courses") {
      newUrl = "?mode=courses";
      localStorage.setItem("appMode", "courses");
    } else if (newView === "auth") {
      localStorage.removeItem("appMode");
    } else {
      newUrl = "?mode=admin";
      localStorage.setItem("appMode", "admin");
    }
    window.history.pushState({}, "", newUrl);
  };

  const handleLogout = () => {
    localStorage.removeItem("appMode");
    signOut(auth).then(() => handleNavigate("landing"));
  };

  useEffect(() => {
    const initAuth = async () => {
      if (typeof window !== "undefined" && window.__initial_auth_token) {
        try {
          if (!auth.currentUser) await signInWithCustomToken(auth, window.__initial_auth_token);
        } catch (e) {
          console.error("Error token:", e);
        }
      }
    };
    initAuth();

    const unsubscribe = onAuthStateChanged(auth, async (u) => {
      if (u) {
        if (u.isAnonymous) {
          setUserRole("anonymous");
          setUser(u);
          setAuthChecking(false);
        } else {
          try {
            const userDocRef = doc(db, "users", u.uid);
            const userDocSnap = await getDoc(userDocRef);
            if (userDocSnap.exists()) {
              const data = userDocSnap.data();
              setUserRole(data.role === "cocina" ? "kitchen" : data.role || "cliente");
              setUserData(data);
            } else {
              setUserRole("cliente");
              setUserData({ role: "cliente", cursosComprados: [] });
            }
          } catch (error) {
            console.error("Error cargando perfil:", error);
            setUserRole("cliente"); 
          }
          setUser(u);
          setAuthChecking(false);
        }
      } else {
        setUser(null);
        setUserRole(null);
        setUserData(null);
        setAuthChecking(false);
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!authChecking && !user && (view === "public_store" || view === "courses_store")) {
      signInAnonymously(auth).catch(err => console.error("Error anónimo:", err));
    }
  }, [view, user, authChecking]);

  // --- REDIRECCIÓN INTELIGENTE CORREGIDA ---
  useEffect(() => {
    if (
      !authChecking &&
      user &&
      !user.isAnonymous &&
      view === "auth" // SOLUCIÓN: Solo bloqueamos el acceso si intentan entrar al Login
    ) {
      if (userRole === "admin" || userRole === "kitchen") {
        handleNavigate("orders");
      } else if (userRole === "cliente") {
        handleNavigate("my_courses");
      }
    }
  }, [user, authChecking, view, userRole]);

  useEffect(() => {
    const configRef = doc(db, "artifacts", appId, "public", "data", "config", "global");
    const unSubConfig = onSnapshot(configRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        setGlobalConfig((prev) => ({
          modules: { ...prev.modules, ...(data.modules || {}) }, 
          general: { ...prev.general, ...(data.general || {}) },
          theme: { ...prev.theme, ...(data.theme || {}) }, 
          whatsapp: { ...prev.whatsapp, ...(data.whatsapp || {}) },
          categories: { ...prev.categories, ...(data.categories || {}) },
          landing: { ...prev.landing, ...(data.landing || {}) },
          courses: data.courses || [], 
        }));
      }
      setConfigLoaded(true); 
    }, (error) => {
      console.error("Error cargando config", error);
      setConfigLoaded(true);
    });
    return () => unSubConfig();
  }, []);

  useEffect(() => {
    const q = query(collection(db, "artifacts", appId, "public", "data", "categories"));
    return onSnapshot(q, (snap) =>
      setCategories(snap.docs.map((d) => ({ id: d.id, ...d.data() })).sort((a, b) => a.name.localeCompare(b.name)))
    );
  }, []);

  useEffect(() => {
    const qP = query(collection(db, "artifacts", appId, "public", "data", "products"));
    const unSubP = onSnapshot(qP, (snap) =>
      setProducts(snap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((p) => p.storeId === STORE_NAME))
    );

    let unSubC = () => {};
    if (user && !user.isAnonymous && (userRole === "admin" || userRole === "kitchen")) {
      const qC = query(collection(db, "artifacts", appId, "public", "data", "clients"));
      unSubC = onSnapshot(qC, (snap) =>
        setClients(snap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((c) => c.storeId === STORE_NAME))
      );
    }
    return () => { unSubP(); unSubC(); };
  }, [user, userRole]);

  useEffect(() => {
    if (!user || user.isAnonymous || userRole === "cliente") return;
    const ordersQuery = query(collection(db, "artifacts", appId, "public", "data", "orders"));
    const unsubscribe = onSnapshot(ordersQuery, (snap) => {
      const allOrders = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() })).filter((o) => o.storeId === STORE_NAME);
      const dateLimit = new Date();
      dateLimit.setDate(dateLimit.getDate() - 30);
      const dateLimitStr = dateLimit.toISOString().split("T")[0];
      const filteredOrders = isHistoryLoaded ? allOrders : allOrders.filter((o) => {
        if (!o.date) return true;
        return o.date >= dateLimitStr;
      });
      setOrders(filteredOrders.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0)));
    });
    return () => unsubscribe();
  }, [user, userRole, isHistoryLoaded]);

  const loadFullHistory = () => setIsHistoryLoaded(true);

  if (authChecking || !configLoaded) return <Loading />;

  if (view === "landing") {
    return (
      <div className="relative">
        <TailwindInjector theme={globalConfig.theme} />
        <Suspense fallback={<Loading />}>
          <LandingPage
            onGoToStore={() => handleNavigate("public_store")}
            onGoToAuth={() => handleNavigate("auth")}
            onGoToCourses={globalConfig.modules?.courses !== false ? () => handleNavigate("courses_store") : null} 
            products={products}
            globalConfig={globalConfig}
          />
        </Suspense>
        {/* BOTÓN FLOTANTE PARA ADMINS LOGUEADOS EN LA LANDING PAGE */}
        {user && !user.isAnonymous && (userRole === "admin" || userRole === "kitchen") && (
          <button onClick={() => handleNavigate("orders")} className="fixed bottom-4 right-4 bg-black/90 text-white px-4 py-3 rounded-full text-xs font-bold z-50 hover:bg-black transition-all shadow-xl flex items-center gap-2">
            ⚙️ Panel Admin
          </button>
        )}
      </div>
    );
  }

  if (view === "auth" || ((!user || user.isAnonymous) && view !== "public_store" && view !== "courses_store")) {
    return (
      <>
        <TailwindInjector theme={globalConfig.theme} />
        <Suspense fallback={<Loading />}><AuthView onLogin={() => {}} globalConfig={globalConfig} /></Suspense>
      </>
    );
  }

  if (view === "public_store") {
    const isAdmin = user && !user.isAnonymous && userRole === "admin";
    return (
      <div className="w-full min-h-screen shadow-2xl relative" style={{ backgroundColor: 'var(--color-background)' }}>
        <TailwindInjector theme={globalConfig.theme} />
        <Suspense fallback={<Loading />}>
          <PublicStoreView db={db} products={products} categories={categories} onGoHome={() => handleNavigate("landing")} onGoAdmin={() => handleNavigate("orders")} isAdmin={isAdmin} globalConfig={globalConfig} />
        </Suspense>
      </div>
    );
  }

  if (view === "courses_store") {
    if (globalConfig.modules?.courses === false) { handleNavigate("landing"); return null; }
    return (
      <div className="w-full min-h-screen shadow-2xl relative" style={{ backgroundColor: 'var(--color-background)' }}>
        <TailwindInjector theme={globalConfig.theme} />
        <Suspense fallback={<Loading />}><CourseCatalogView db={db} user={user} onGoHome={() => handleNavigate("landing")} onGoToAuth={() => handleNavigate("auth")} globalConfig={globalConfig} /></Suspense>
      </div>
    );
  }

  return (
    <div className="min-h-screen font-sans text-gray-800" style={{ backgroundColor: 'var(--color-background)' }}>
      <TailwindInjector theme={globalConfig.theme} />
      <div className="w-full min-h-screen bg-white shadow-2xl relative flex flex-col md:max-w-full md:mx-0 md:pl-72">
        <Header onLogout={handleLogout} userRole={userRole} onOpenMenu={() => setIsMenuOpen(true)} globalConfig={globalConfig} />
        
        <Sidebar
          isOpen={isMenuOpen}
          onClose={() => setIsMenuOpen(false)}
          setView={handleNavigate}
          activeView={view}
          userRole={userRole}
          onLogout={handleLogout}
          installPwa={user && !user.isAnonymous ? handleInstallClick : null}
          globalConfig={globalConfig} 
        />

        <div className="flex-1 overflow-hidden flex flex-col relative">
          <div className="flex-1 overflow-y-auto w-full h-full" style={{ backgroundColor: 'var(--color-background)' }}>
            <Suspense fallback={<Loading />}>
              {view === "orders" && (userRole === "admin" || userRole === "kitchen") && <OrdersView db={db} products={products} clients={clients} categories={categories} orders={orders} isHistoryLoaded={isHistoryLoaded} onLoadHistory={loadFullHistory} globalConfig={globalConfig} />}
              {view === "kitchen" && (userRole === "admin" || userRole === "kitchen") && <KitchenView db={db} products={products} categories={categories} />}
              {view === "pantry" && (userRole === "admin" || userRole === "kitchen") && globalConfig.modules?.pantry !== false && <PantryView db={db} globalConfig={globalConfig} />}
              {view === "freezer" && (userRole === "admin" || userRole === "kitchen") && globalConfig.modules?.freezer !== false && <FreezerView db={db} products={products} />}
              {view === "stats" && userRole === "admin" && globalConfig.modules?.finances !== false && <StatsView db={db} orders={orders} isHistoryLoaded={isHistoryLoaded} onLoadHistory={loadFullHistory} globalConfig={globalConfig} />}
              {view === "menu" && userRole === "admin" && <MenuView db={db} categories={categories} />}
              {view === "clients" && userRole === "admin" && globalConfig.modules?.clients !== false && <ClientsView db={db} />}
              {view === "config" && userRole === "admin" && <ConfigView db={db} />}
              {view === "my_courses" && userRole === "cliente" && globalConfig.modules?.courses !== false && <MyCoursesView userData={userData} globalConfig={globalConfig} />}
            </Suspense>
          </div>
        </div>
      </div>
    </div>
  );
};

export default App;
